import { PaymentMethodType } from './payment-types.types';
import { Currency, Timestamps, Versioned } from './shared.types';

export enum AccountOwnerType {
  USER = 'USER',
  CUSTOMER = 'CUSTOMER',
  SYSTEM = 'SYSTEM',
}

export enum TransactionFlow {
  INFLOW = 'INFLOW',
  OUTFLOW = 'OUTFLOW',
}

export enum TransactionCategory {
  SALES_RECEIPT = 'SALES_RECEIPT',
  SALES_REFUND = 'SALES_REFUND',
  DEBT_PAYMENT = 'DEBT_PAYMENT',
  LOAN_PAYMENT = 'LOAN_PAYMENT',
  LOAN_GIVEN = 'LOAN_GIVEN',
  TRANSFER_IN = 'TRANSFER_IN',
  TRANSFER_OUT = 'TRANSFER_OUT',
  INVESTMENT = 'INVESTMENT',
  DIVIDEND = 'DIVIDEND',
  SUPPLIER_PAYMENT = 'SUPPLIER_PAYMENT',
  SUPPLIER_REFUND = 'SUPPLIER_REFUND',
  EXPENSE = 'EXPENSE',
  CORRECTION = 'CORRECTION',
  OPENING_CORRECTION = 'OPENING_CORRECTION',
  CLOSING_CORRECTION = 'CLOSING_CORRECTION',
  DEBT_PAYMENT_REVERSAL = 'DEBT_PAYMENT_REVERSAL',
  LOAN_PAYMENT_REVERSAL = 'LOAN_PAYMENT_REVERSAL',
  LOAN_GIVEN_REVERSAL = 'LOAN_GIVEN_REVERSAL',
  EXPENSE_REVERSAL = 'EXPENSE_REVERSAL',
  DIVIDEND_REVERSAL = 'DIVIDEND_REVERSAL',
  INVESTMENT_REVERSAL = 'INVESTMENT_REVERSAL',
}

export interface StoreSnapshotsResult {
  at_time: string;
  snapshots: AccountSnapshotsByPaymentMethod[];
}

export interface CommentRef {
  comment_id: string;
  body: string;
  commenter: PersonRef;
  created_at: string;
}

export interface OriginEntityRef {
  entity_type: string;
  entity_id: string;
  description?: string;
  /**
   * Optional free-form key/value metadata for the origin entity. Used to
   * thread extra machine-readable identifiers (e.g. `debt_id` on a
   * DEBT_PAYMENT-origined transaction) without overloading `entity_id`.
   */
  metadata?: Record<string, string>;
}

export interface PersonRef {
  id: string;
  type: AccountOwnerType;
  name?: string;
  note?: string;
}

export interface AccountTransaction extends Timestamps {
  account_id: string;
  owner: PersonRef;
  merchant_id: string;
  store_id: string;
  shift_id?: string;
  flow: TransactionFlow;
  category: TransactionCategory;
  payment_type: PaymentMethodType;
  payment_method_id: string;
  payment_method_metadata?: Record<string, any>;
  origin_entity?: OriginEntityRef;
  creator: PersonRef;
  approver?: PersonRef | null;
  forced?: boolean;
  without_consent?: boolean;
  amount: number;
  currency: Currency;
  comments?: CommentRef[];
  approved_at?: string | null;
  involved_parties?: PersonRef[];
  version: number;
  reverses_transaction_id?: string;
  reversed_by_transaction_id?: string;
  reversal_reason?: string;
}

export interface AccountSnapshotDBItem extends AccountSnapshot {
  /**
   * Main table keys
   * Partitioned by merchant + store for natural "by store" queries.
   */
  PK: string;
  SK: string;
  GSI1PK: string;
  GSI1SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
}

export interface AccountTransactionDBItem extends AccountTransaction {
  PK: string;
  SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
  GSI1PK: string;
  GSI1SK: string;
  GSI2PK?: string;
  GSI2SK?: string;
  GSI3PK: string;
  GSI3SK: string;
  GSI4PK?: string;
  GSI4SK?: string;
  GSI5PK?: string;
  GSI5SK?: string;
  GSI6PK?: string;
  GSI6SK?: string;
  GSI7PK?: string;
  GSI7SK?: string;
}

export interface SummaryByPaymentMethod {
  payment_type: PaymentMethodType;
  payment_method_id: string;
  total_inflow: number;
  total_outflow: number;
  net_amount: number;
}

export interface AccountTransactionChangeSummary {
  items: AccountTransaction[];
  summary: SummaryByPaymentMethod[];
  cursor?: string;
  count?: number;
  /**
   * Partner transactions resolved for items that carry a reversal link
   * (`reverses_transaction_id` or `reversed_by_transaction_id`) whose
   * counterpart is not already included in `items`.
   */
  reversals?: AccountTransaction[];
}

export interface AccountSnapshotBalances {
  [payment_type: string]: {
    [payment_method_id: string]: number;
  };
}

export interface AccountSnapshot extends Timestamps {
  account_id: string;
  amount: number;
  created_by: 'SYSTEM' | string;
  version: number;
  store_id: string;
  merchant_id: string;
  payment_type: PaymentMethodType;
  payment_method_id: string;
}

export interface AccountSnapshotsByPaymentMethod {
  account_id: string;
  merchant_id: string;
  store_id: string;
  created_at: string;
  account_snapshots: AccountSnapshot[];
}

export type BalancesByPaymentMethod = {
  [payment_method_id: string]: number;
};

export type StoreSnapshots = {
  [account_id: string]: AccountSnapshotsByPaymentMethod[];
};

export interface TransferApproval {
  transfer_id: string;
  approver: PersonRef;
  approved_at: string;
  notes?: string;
  transferer_owner?: PersonRef;
  transferee_owner?: PersonRef;
  currency?: Currency;
  shift_id?: string;
}

export interface TransferAudit {
  status: TransferStatus;
  created_at: string;
  actor?: PersonRef;
  notes?: string;
}

export enum TransferStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED',
}

export interface TransferRequest extends Timestamps, Versioned {
  transfer_id: string;
  merchant_id: string;
  store_id: string;
  transferer_account_id: string;
  transferee_account_id: string;
  amount: number;
  payment_type: PaymentMethodType;
  payment_method_id: string;
  status: TransferStatus;
  forced?: boolean;
  without_consent?: boolean;
  approvals: TransferApproval[];
  rejection_reason?: string;
  shift_id?: string;
  currency: Currency;
  transferer_owner?: PersonRef;
  transferee_owner?: PersonRef;
  creator?: PersonRef;
  audit_history?: TransferAudit[];
  comments?: CommentRef[];
  /**
   * Set while an approval moves the money (claimed while PENDING, removed
   * when it is APPROVED), so a second approval, a reject or a cancel
   * cannot run at the same time. Optional; missing = not claimed.
   */
  approval_claimed_at?: string | null;
  approval_claimed_by_id?: string | null;
}

export interface CreateTransferRequest {
  merchant_id: string;
  store_id: string;
  transferer_account_id: string;
  transferee_account_id: string;
  amount: number;
  payment_type: PaymentMethodType;
  payment_method_id: string;
  forced?: boolean;
  without_consent?: boolean;
  shift_id?: string;
  currency: Currency;
  transferer_owner: PersonRef;
  transferee_owner: PersonRef;
  creator: PersonRef;
  approver?: PersonRef;
}

export interface TransactionIngestEvent {
  source_system: string;
  source_reference: string;
  payload: AccountTransaction;
}

export interface TransferEvent {
  source_system: string;
  transfer: TransferRequest;
}

export interface SnapshotRequestEvent {
  account_id: string;
  trigger: 'AUTO' | 'MANUAL';
}

export interface TransferRequestDBItem extends TransferRequest {
  /** --------------
   * MAIN TABLE KEYS
   * --------------*/
  PK: string;
  // PK: MERCHANT#<merchant_id>#STORE#<store_id>

  SK: string;
  // SK: TRANSFER#DATE#<created_at>#TRANSFER#<transfer_id>

  /** --------------
   * ENTITY INDEX (optional; for full scans by entity type)
   * --------------*/
  EntityIndexPK: string;
  // EntityIndexPK: TRANSFER

  EntityIndexSK: string;
  // EntityIndexSK: MERCHANT#<merchant_id>#STORE#<store_id>#DATE#<created_at>#TRANSFER#<transfer_id>

  /** --------------
   * GSI1 — BY TRANSFERER (with date sorting)
   * --------------*/
  GSI1PK: string;
  // GSI1PK: TRANSFERER#<transferer_account_id>

  GSI1SK: string;
  // GSI1SK: DATE#<created_at>#MERCHANT#<merchant_id>#STORE#<store_id>#TRANSFER#<transfer_id>

  /** --------------
   * GSI2 — BY TRANSFEREE (with date sorting)
   * --------------*/
  GSI2PK: string;
  // GSI2PK: TRANSFEREE#<transferee_account_id>

  GSI2SK: string;
  // GSI2SK: DATE#<created_at>#MERCHANT#<merchant_id>#STORE#<store_id>#TRANSFER#<transfer_id>

  /** --------------
   * GSI3 — BY STATUS (PENDING, APPROVED, etc.)
   * --------------*/
  GSI3PK: string;
  // GSI3PK: STATUS#<status>

  GSI3SK: string;
  // GSI3SK: DATE#<created_at>#MERCHANT#<merchant_id>#STORE#<store_id>#TRANSFER#<transfer_id>
}

export interface ListAccountTransactionsBase {
  cursor?: string;
  limit?: number;
  start_date?: string;
  end_date?: string;
}

export interface ListAccountTransactionsByStore
  extends ListAccountTransactionsBase {
  merchant_id: string;
  store_id: string;
}

export interface ListAccountTransactionsByOwner
  extends ListAccountTransactionsBase {
  owner_id: string;
  owner_type: AccountOwnerType;
}

export interface ListAccountTransactionsByShift
  extends ListAccountTransactionsBase {
  shift_id: string;
}

export interface AccountTransactionsPage {
  items: AccountTransaction[];
  cursor?: string;
  count?: number;
}

export interface CreateAccountTransaction {
  account_id: string;
  owner: PersonRef;
  merchant_id: string;
  store_id: string;
  shift_id?: string;
  flow: TransactionFlow;
  category: TransactionCategory;
  payment_type: PaymentMethodType;
  payment_method_id: string;
  payment_method_metadata?: Record<string, any>;
  origin_entity?: OriginEntityRef;
  creator: PersonRef;
  approver?: PersonRef | null;
  forced?: boolean;
  without_consent?: boolean;
  amount: number;
  currency: Currency;
  comments?: CommentRef[];
  approved_at?: string | null;
  involved_parties?: PersonRef[];
  reverses_transaction_id?: string;
  reversal_reason?: string;
}

export interface GetAccountsByStore {
  merchant_id: string;
  store_id: string;
  start_date?: string;
  end_date?: string;
  cursor?: string;
  limit?: number;
}

export interface GetAccountsByOwner {
  owner_id: string;
  owner_type: AccountOwnerType;
  start_date?: string;
  end_date?: string;
  cursor?: string;
  limit?: number;
}

export interface GetAccountsByShift {
  shift_id: string;
  start_date?: string;
  end_date?: string;
  cursor?: string;
  limit?: number;
}

export interface GetAccountsByAccount {
  account_id: string;
  store_id: string;
  start_date?: string;
  end_date?: string;
  cursor?: string;
  limit?: number;
}

export interface GetTransfers {
  merchant_id: string;
  store_id: string;
  transferer_id?: string;
  transferee_id?: string;
  status?: TransferStatus;
  date_from?: string;
  date_to?: string;
  cursor?: string;
  limit?: number;
}

export interface RejectTransferRequest {
  transfer_id: string;
  reason: string;
  actor: PersonRef;
  shift_id?: string;
}

export interface CancelTransferRequest {
  transfer_id: string;
  reason: string;
  actor: PersonRef;
  shift_id?: string;
}

export interface ReverseLoanGivenRequest {
  merchant_id: string;
  store_id: string;
  debt_id: string;
  creator: PersonRef;
  owner?: PersonRef;
  shift_id?: string;
  reason: string;
  currency?: Currency;
}

/**
 * Generic request to reverse any previously recorded AccountTransaction.
 * The original transaction is located by its composite key:
 * (account_id, payment_type, payment_method_id, store_id, version).
 * The reversal category is derived automatically from the original category.
 */
export interface ReverseAccountTransactionRequest {
  merchant_id: string;
  store_id: string;
  account_id: string;
  payment_type: PaymentMethodType;
  payment_method_id: string;
  version: number;
  creator: PersonRef;
  shift_id?: string;
  reason: string;
}

export interface ReverseDebtPaymentTransactionRequest {
  merchant_id: string;
  store_id: string;
  payment_id: string;
  debt_id: string;
  creator: PersonRef;
  shift_id?: string;
  reason: string;
}
